/**
 * Which facility a new task is created for (`created_by_facility_id`): what the Task App
 * lists a campus's tasks under. A single-facility screen sends its own facility; a
 * campus-wide screen asks this, and prompts only when the answer is not implied.
 */

export interface CreatedByFacilityOption {
  id: string | number;
  name: string;
  facility_alias?: string | null;
}

export type CreatedByFacilityResolution =
  /** No prompt. `facilityId` null leaves it to the server (the user's only campus facility, else the trailer's). */
  | { kind: 'implicit'; facilityId: string | null }
  | { kind: 'ask'; options: CreatedByFacilityOption[]; defaultId: string };

export interface CreatedByFacilityRequest {
  /** Every facility of the campus the task is in. */
  campusFacilities: CreatedByFacilityOption[];
  /** The user's own facilities (locations_users). Only these are offered. */
  userFacilityIds: Array<string | number>;
  /** Preselected in the prompt, first match wins: the trailer's facility, then the destination's. */
  preferredFacilityIds?: Array<string | number | null | undefined>;
}

export function resolveCreatedByFacility({
  campusFacilities,
  userFacilityIds,
  preferredFacilityIds = []
}: CreatedByFacilityRequest): CreatedByFacilityResolution {
  const userIds = new Set(userFacilityIds.map(String));
  const options = campusFacilities.filter((f) => userIds.has(String(f.id)));
  const optionIds = options.map((f) => String(f.id));

  // A page's facility filter implies nothing: the page still shows the whole campus.
  if (options.length <= 1) {
    return { kind: 'implicit', facilityId: optionIds[0] ?? null };
  }

  const preferred = preferredFacilityIds
    .filter((id): id is string | number => id != null && String(id) !== '')
    .map(String)
    .find((id) => optionIds.includes(id));

  return { kind: 'ask', options, defaultId: preferred ?? optionIds[0] };
}

/** Adds `created_by_facility_id` to mutation variables, only when there is one. */
export function withCreatedByFacility<T extends Record<string, any>>(
  variables: T,
  facilityId: string | number | null | undefined
): T & { created_by_facility_id?: string } {
  if (facilityId == null || String(facilityId) === '') return variables;
  return { ...variables, created_by_facility_id: String(facilityId) };
}

/**
 * The campus and the user's facilities in it, from the campus destination lists the move
 * screens already load: `canDock` is the user's own facility (locations_users).
 */
export function campusFromDestinationFacilities(
  facilities: Array<{
    facilityId: string;
    facilityName: string;
    facilityAlias?: string | null;
    canDock?: boolean;
  }>
): Pick<CreatedByFacilityRequest, 'campusFacilities' | 'userFacilityIds'> {
  return {
    campusFacilities: facilities.map((f) => ({
      id: f.facilityId,
      name: f.facilityName,
      facility_alias: f.facilityAlias
    })),
    userFacilityIds: facilities.filter((f) => f.canDock).map((f) => f.facilityId)
  };
}
